'use client'

import { useCallback, useSyncExternalStore } from 'react'

/**
 * Whether a media query matches, kept in sync with the viewport.
 *
 * `serverValue` is what the server render (and the first client render, for
 * hydration) assumes; pick the value whose markup is safe to ship before the
 * real answer is known. jsdom has no matchMedia and gets the same value.
 */
export function useMediaQuery(query: string, serverValue: boolean): boolean {
  const subscribe = useCallback(
    (onChange: () => void) => {
      if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return () => {}
      const mq = window.matchMedia(query)
      mq.addEventListener('change', onChange)
      return () => mq.removeEventListener('change', onChange)
    },
    [query],
  )
  const getSnapshot = () =>
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia(query).matches
      : serverValue
  return useSyncExternalStore(subscribe, getSnapshot, () => serverValue)
}
